import React, { useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  RefreshCw,
  Trash2,
  Upload,
  X
} from 'lucide-react';

const IMPORT_FIELDS = [
  { key: 'firstName', labelKey: 'bulkImport.fields.firstName', defaultLabel: 'First Name' },
  { key: 'lastName', labelKey: 'bulkImport.fields.lastName', defaultLabel: 'Last Name' },
  { key: 'jobTitle', labelKey: 'bulkImport.fields.jobTitle', defaultLabel: 'Job Title' },
  { key: 'location', labelKey: 'bulkImport.fields.location', defaultLabel: 'Location (Display)' },
  { key: 'bio', labelKey: 'bulkImport.fields.bio', defaultLabel: 'Bio' },
  { key: 'email', labelKey: 'bulkImport.fields.email', defaultLabel: 'Email' },
  { key: 'password', labelKey: 'bulkImport.fields.password', defaultLabel: 'Password' },
  { key: 'role', labelKey: 'bulkImport.fields.role', defaultLabel: 'Role' },
  { key: 'department', labelKey: 'bulkImport.fields.department', defaultLabel: 'Department' },
  { key: 'phone1', labelKey: 'bulkImport.fields.phone1', defaultLabel: 'Phone 1' },
  { key: 'phone2', labelKey: 'bulkImport.fields.phone2', defaultLabel: 'Phone 2' },
  { key: 'street', labelKey: 'bulkImport.fields.street', defaultLabel: 'Street' },
  { key: 'postalCode', labelKey: 'bulkImport.fields.postalCode', defaultLabel: 'Postal Code' },
  { key: 'city', labelKey: 'bulkImport.fields.city', defaultLabel: 'City' },
  { key: 'country', labelKey: 'bulkImport.fields.country', defaultLabel: 'Country' },
  { key: 'showRoute', labelKey: 'bulkImport.fields.showRoute', defaultLabel: 'Show route' },
  { key: 'website', labelKey: 'bulkImport.fields.website', defaultLabel: 'Website' },
  { key: 'linkedin', labelKey: 'bulkImport.fields.linkedin', defaultLabel: 'LinkedIn URL' },
  { key: 'twitter', labelKey: 'bulkImport.fields.twitter', defaultLabel: 'Twitter / X' },
  { key: 'instagram', labelKey: 'bulkImport.fields.instagram', defaultLabel: 'Instagram' },
  { key: 'github', labelKey: 'bulkImport.fields.github', defaultLabel: 'Github' }
];

const HEADER_ALIASES = {
  firstName: ['first name', 'firstname', 'vorname'],
  lastName: ['last name', 'lastname', 'surname', 'nachname'],
  jobTitle: ['job title', 'title', 'position', 'jobtitle'],
  location: ['location (display)', 'location', 'standort', 'ort'],
  bio: ['bio', 'beschreibung', 'about'],
  email: ['email', 'e-mail', 'mail'],
  password: ['password', 'passwort', 'initial password', 'temporary password'],
  role: ['role', 'rolle', 'user role'],
  department: ['department', 'abteilung', 'team'],
  phone1: ['phone 1', 'phone1', 'telefon 1', 'telefon1', 'phone'],
  phone2: ['phone 2', 'phone2', 'telefon 2', 'telefon2'],
  street: ['street', 'strasse', 'straße'],
  postalCode: ['postal code', 'postalcode', 'zip', 'plz'],
  city: ['city', 'stadt'],
  country: ['country', 'land'],
  showRoute: ['show route', 'show route button', 'route', 'showroute'],
  website: ['website', 'webseite', 'url'],
  linkedin: ['linkedin url', 'linkedin'],
  twitter: ['twitter / x', 'twitter', 'x', 'twitter/x'],
  instagram: ['instagram'],
  github: ['github', 'git hub']
};

const BOOLEAN_TRUE_VALUES = new Set(['true', '1', 'yes', 'y', 'ja', 'x', 'on']);
const BOOLEAN_FALSE_VALUES = new Set(['false', '0', 'no', 'n', 'nein', 'off']);
const ROLE_OPTIONS = ['member', 'department_lead', 'admin', 'owner'];
const URL_FIELDS = new Set(['website', 'linkedin', 'twitter', 'instagram', 'github']);
const DEFAULT_FALSE_ERRORS = Object.fromEntries(IMPORT_FIELDS.map((field) => [field.key, '']));

const normalizeHeader = (value = '') => String(value)
  .normalize('NFKD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

const normalizeCellValue = (value) => String(value ?? '').replace(/\uFEFF/g, '').trim();

const parseBooleanCell = (value) => {
  const normalized = normalizeCellValue(value).toLowerCase();
  if (!normalized) return true;
  if (BOOLEAN_TRUE_VALUES.has(normalized)) return true;
  if (BOOLEAN_FALSE_VALUES.has(normalized)) return false;
  return null;
};

const isBlankRow = (row) => IMPORT_FIELDS.every((field) => !normalizeCellValue(row[field.key]));

const isValidUrl = (value) => {
  const normalized = normalizeCellValue(value);
  if (!normalized) return true;
  try {
    const parsed = new URL(normalized);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch (e) {
    return false;
  }
};

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizeCellValue(value));
const normalizeRoleValue = (value) => normalizeCellValue(value).toLowerCase().replace(/\s+/g, '_');

const buildTemplateWorkbook = (t) => {
  const headers = IMPORT_FIELDS.reduce((acc, field) => {
    acc[field.key] = t(field.labelKey, { defaultValue: field.defaultLabel });
    return acc;
  }, {});

  const templateRows = [
    {
      [headers.firstName]: 'Jürgen',
      [headers.lastName]: 'Müller',
      [headers.jobTitle]: 'Sales Manager',
      [headers.location]: 'Berlin, Germany',
      [headers.bio]: 'Short profile text for the imported card.',
      [headers.email]: 'juergen.mueller@example.com',
      [headers.password]: 'Welcome123!',
      [headers.role]: 'member',
      [headers.department]: 'Sales',
      [headers.phone1]: '+49 30 123456',
      [headers.phone2]: '',
      [headers.street]: 'Musterstraße 1',
      [headers.postalCode]: '10115',
      [headers.city]: 'Berlin',
      [headers.country]: 'DE',
      [headers.showRoute]: 'true',
      [headers.website]: 'https://example.com',
      [headers.linkedin]: 'https://linkedin.com/in/juergen-mueller',
      [headers.twitter]: '',
      [headers.instagram]: '',
      [headers.github]: ''
    },
    {
      [headers.firstName]: 'Anna',
      [headers.lastName]: 'Schneider',
      [headers.jobTitle]: 'Head of Consulting',
      [headers.location]: 'Hamburg, Germany',
      [headers.bio]: 'Example row for a department lead import.',
      [headers.email]: 'anna.schneider@example.com',
      [headers.password]: 'Consulting123!',
      [headers.role]: 'department_lead',
      [headers.department]: 'Consulting',
      [headers.phone1]: '+49 40 987654',
      [headers.phone2]: '',
      [headers.street]: 'Elbchaussee 5',
      [headers.postalCode]: '22765',
      [headers.city]: 'Hamburg',
      [headers.country]: 'DE',
      [headers.showRoute]: 'false',
      [headers.website]: 'https://example.org',
      [headers.linkedin]: 'https://linkedin.com/in/anna-schneider',
      [headers.twitter]: 'https://x.com/anna_schneider',
      [headers.instagram]: '',
      [headers.github]: ''
    }
  ];

  const worksheet = XLSX.utils.json_to_sheet(templateRows, {
    header: IMPORT_FIELDS.map((field) => headers[field.key])
  });

  const columnWidths = IMPORT_FIELDS.map((field) => ({
    wch: Math.max(headers[field.key].length + 2, 18)
  }));
  worksheet['!cols'] = columnWidths;

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    t('bulkImport.template.sheetName', { defaultValue: 'Import Template' })
  );

  return workbook;
};

const decodeCsvText = (arrayBuffer) => {
  const bytes = new Uint8Array(arrayBuffer);
  const hasUtf8Bom = bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf;
  const candidates = hasUtf8Bom ? ['utf-8'] : ['utf-8', 'windows-1252', 'iso-8859-1'];

  let best = '';
  let bestScore = Number.POSITIVE_INFINITY;

  for (const encoding of candidates) {
    try {
      const decoded = new TextDecoder(encoding, { fatal: false }).decode(bytes);
      const replacementCount = (decoded.match(/\uFFFD/g) || []).length;
      const delimiterScore = (decoded.match(/[;,]/g) || []).length > 0 ? 0 : 10;
      const newlineScore = decoded.includes('\n') ? 0 : 10;
      const score = replacementCount * 100 + delimiterScore + newlineScore;
      if (score < bestScore) {
        best = decoded;
        bestScore = score;
      }
      if (score === 0) break;
    } catch (e) {
      // Ignore and try the next decoder.
    }
  }

  return best.replace(/^\uFEFF/, '');
};

const buildHeaderMap = (headerRow = []) => {
  const normalizedHeaders = headerRow.map((cell) => normalizeHeader(cell));
  const headerMap = {};

  IMPORT_FIELDS.forEach((field) => {
    const aliases = HEADER_ALIASES[field.key] || [];
    const index = normalizedHeaders.findIndex((header) => aliases.includes(header));
    if (index !== -1) {
      headerMap[field.key] = index;
    }
  });

  return headerMap;
};

const parseWorkbookRows = async (file) => {
  const fileName = file?.name || '';
  const isCsv = fileName.toLowerCase().endsWith('.csv');
  const arrayBuffer = await file.arrayBuffer();

  const workbook = isCsv
    ? XLSX.read(decodeCsvText(arrayBuffer), { type: 'string', raw: false, WTF: false })
    : XLSX.read(arrayBuffer, { type: 'array', raw: false, WTF: false });

  const firstSheetName = workbook.SheetNames[0];
  if (!firstSheetName) {
    return [];
  }

  const sheet = workbook.Sheets[firstSheetName];
  const grid = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    defval: '',
    raw: false,
    blankrows: false
  });

  if (!Array.isArray(grid) || grid.length === 0) {
    return [];
  }

  const [headerRow, ...dataRows] = grid;
  const headerMap = buildHeaderMap(headerRow);

  return dataRows
    .map((cells, index) => {
      const mappedRow = IMPORT_FIELDS.reduce((acc, field) => {
        const cellIndex = headerMap[field.key];
        acc[field.key] = cellIndex === undefined ? '' : normalizeCellValue(cells[cellIndex]);
        return acc;
      }, {});

      return {
        id: `${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`,
        sourceRow: index + 2,
        ...mappedRow
      };
    })
    .filter((row) => !isBlankRow(row));
};

const validateImportRows = (rows, t) => {
  const seenEmails = new Map();

  rows.forEach((row) => {
    const email = normalizeCellValue(row.email).toLowerCase();
    if (!email) return;
    seenEmails.set(email, (seenEmails.get(email) || 0) + 1);
  });

  return rows.map((row) => {
    const errors = { ...DEFAULT_FALSE_ERRORS };
    const email = normalizeCellValue(row.email).toLowerCase();
    const password = normalizeCellValue(row.password);
    const showRoute = parseBooleanCell(row.showRoute);
    const role = normalizeRoleValue(row.role || 'member');

    if (!email) {
      errors.email = t('bulkImport.validation.emailRequired', { defaultValue: 'Email is required' });
    } else if (!isValidEmail(email)) {
      errors.email = t('bulkImport.validation.emailInvalid', { defaultValue: 'Invalid email address' });
    } else if ((seenEmails.get(email) || 0) > 1) {
      errors.email = t('bulkImport.validation.emailDuplicate', { defaultValue: 'Duplicate email in import file' });
    }

    if (password && password.length < 8) {
      errors.password = t('bulkImport.validation.passwordTooShort', {
        defaultValue: 'Password must be at least 8 characters long'
      });
    }

    if (row.showRoute && showRoute === null) {
      errors.showRoute = t('bulkImport.validation.booleanInvalid', { defaultValue: 'Use true/false, yes/no, 1/0' });
    }

    if (row.role && !ROLE_OPTIONS.includes(role)) {
      errors.role = t('bulkImport.validation.roleInvalid', { defaultValue: 'Invalid role' });
    }

    if (role === 'department_lead' && !normalizeCellValue(row.department)) {
      errors.department = t('bulkImport.validation.departmentRequiredForLead', {
        defaultValue: 'Department is required for department leads'
      });
    }

    URL_FIELDS.forEach((field) => {
      if (row[field] && !isValidUrl(row[field])) {
        errors[field] = t('bulkImport.validation.urlInvalid', { defaultValue: 'Invalid URL' });
      }
    });

    const errorMessages = Object.values(errors).filter(Boolean);

    return {
      ...row,
      email,
      role,
      normalizedShowRoute: showRoute === null ? true : showRoute,
      errors,
      errorMessages,
      isValid: errorMessages.length === 0
    };
  });
};

export const mapImportRowToCardPayload = (row, organisationName = '') => ({
  personal: {
    firstName: normalizeCellValue(row.firstName),
    lastName: normalizeCellValue(row.lastName),
    title: normalizeCellValue(row.jobTitle),
    company: organisationName,
    bio: normalizeCellValue(row.bio),
    location: normalizeCellValue(row.location)
  },
  contact: {
    email: normalizeCellValue(row.email).toLowerCase(),
    phone: normalizeCellValue(row.phone1),
    phoneLabel: '',
    phoneSecondary: normalizeCellValue(row.phone2),
    phoneSecondaryLabel: '',
    address: [
      normalizeCellValue(row.street),
      [normalizeCellValue(row.postalCode), normalizeCellValue(row.city)].filter(Boolean).join(' ').trim(),
      normalizeCellValue(row.country)
    ].filter(Boolean).join(', '),
    addressStreet: normalizeCellValue(row.street),
    addressPostalCode: normalizeCellValue(row.postalCode),
    addressCity: normalizeCellValue(row.city),
    addressCountry: normalizeCellValue(row.country),
    showRouteButton: parseBooleanCell(row.showRoute) === null ? true : parseBooleanCell(row.showRoute),
    website: normalizeCellValue(row.website)
  },
  social: {
    linkedin: normalizeCellValue(row.linkedin),
    twitter: normalizeCellValue(row.twitter),
    instagram: normalizeCellValue(row.instagram),
    github: normalizeCellValue(row.github)
  }
});

const emptyServerResult = {
  importedCount: 0,
  skippedCount: 0,
  errors: []
};

export default function BulkUserImport({
  isOpen,
  onClose,
  apiCall,
  organisationId,
  organisationName,
  showAlert,
  onImported
}) {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState('');
  const [isParsing, setIsParsing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [serverResult, setServerResult] = useState(emptyServerResult);

  const validatedRows = useMemo(() => validateImportRows(rows, t), [rows, t]);
  const hasErrors = validatedRows.some((row) => !row.isValid);
  const totalErrorCount = validatedRows.reduce((sum, row) => sum + row.errorMessages.length, 0);

  if (!isOpen) return null;

  const resetState = () => {
    setRows([]);
    setFileName('');
    setIsParsing(false);
    setIsImporting(false);
    setServerResult(emptyServerResult);
  };

  const handleClose = () => {
    resetState();
    onClose?.();
  };

  const updateCell = (rowId, field, value) => {
    setRows((prev) => prev.map((row) => (
      row.id === rowId ? { ...row, [field]: value } : row
    )));
  };

  const removeRow = (rowId) => {
    setRows((prev) => prev.filter((row) => row.id !== rowId));
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setIsParsing(true);
    setServerResult(emptyServerResult);
    try {
      const parsedRows = await parseWorkbookRows(file);
      setRows(parsedRows);
      setFileName(file.name);

      if (parsedRows.length === 0 && showAlert) {
        showAlert(
          t('bulkImport.alerts.noRows', { defaultValue: 'No importable rows were found in the file.' }),
          'info'
        );
      }
    } catch (error) {
      console.error('Failed to parse bulk import file:', error);
      if (showAlert) {
        showAlert(
          t('bulkImport.alerts.parseFailed', { defaultValue: 'The file could not be parsed.' }),
          'error'
        );
      }
    } finally {
      setIsParsing(false);
    }
  };

  const handleImport = async () => {
    if (!organisationId) {
      showAlert?.(
        t('bulkImport.alerts.organisationRequired', { defaultValue: 'Please select an organisation first.' }),
        'error'
      );
      return;
    }

    if (validatedRows.length === 0) {
      showAlert?.(
        t('bulkImport.alerts.noRows', { defaultValue: 'No importable rows were found in the file.' }),
        'info'
      );
      return;
    }

    if (hasErrors) {
      showAlert?.(
        t('bulkImport.alerts.fixErrors', { defaultValue: 'Please fix the highlighted rows before importing.' }),
        'error'
      );
      return;
    }

    setIsImporting(true);
    try {
      const response = await apiCall('/api/admin/users/bulk-import', {
        method: 'POST',
        body: JSON.stringify({
          rows: validatedRows.map(({ errors, errorMessages, isValid, normalizedShowRoute, id, sourceRow, ...row }) => ({
            ...row,
            role: row.role || 'member',
            showRoute: normalizedShowRoute
          }))
        })
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        showAlert?.(
          payload.error || t('bulkImport.alerts.importFailed', { defaultValue: 'Bulk import failed.' }),
          'error'
        );
        return;
      }

      const result = {
        importedCount: payload.importedCount || 0,
        skippedCount: payload.skippedCount || 0,
        errors: Array.isArray(payload.errors) ? payload.errors : []
      };
      setServerResult(result);

      if (result.importedCount > 0) {
        showAlert?.(
          t('bulkImport.alerts.importSuccess', {
            defaultValue: '{{count}} users were imported successfully.',
            count: result.importedCount
          }),
          'success'
        );
      }

      if (result.skippedCount > 0 && showAlert) {
        showAlert(
          t('bulkImport.alerts.importPartial', {
            defaultValue: '{{count}} rows were skipped. Review the import result below.',
            count: result.skippedCount
          }),
          'warning'
        );
      }

      onImported?.(payload);

      if (result.skippedCount === 0) {
        handleClose();
      }
    } catch (error) {
      console.error('Bulk import failed:', error);
      showAlert?.(
        t('bulkImport.alerts.importError', { defaultValue: 'An error occurred while importing the users.' }),
        'error'
      );
    } finally {
      setIsImporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    const workbook = buildTemplateWorkbook(t);
    XLSX.writeFile(
      workbook,
      t('bulkImport.template.fileName', { defaultValue: 'vcard-bulk-import-template.xlsx' })
    );
  };

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-main dark:bg-main-dark opacity-80" />
      <div className="relative h-full w-full flex items-center justify-center p-4">
        <div className="bg-card dark:bg-card-dark border border-border dark:border-border-dark rounded-card shadow-xl w-full max-w-[95vw] xl:max-w-[1440px] max-h-[92vh] overflow-hidden flex flex-col">
          <div className="px-6 py-5 border-b border-border dark:border-border-dark flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-11 h-11 rounded-full bg-surface dark:bg-surface-dark border border-border dark:border-border-dark flex items-center justify-center">
                  <FileSpreadsheet className="w-5 h-5 text-text-primary dark:text-text-primary-dark" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-text-primary dark:text-text-primary-dark">
                    {t('bulkImport.title', { defaultValue: 'Bulk user import' })}
                  </h2>
                  <p className="text-sm text-text-muted dark:text-text-muted-dark">
                    {t('bulkImport.subtitle', {
                      defaultValue: 'Import users and one card per user into {{organisation}}.',
                      organisation: organisationName || t('common.fields.unknown', { defaultValue: 'Unknown' })
                    })}
                  </p>
                </div>
              </div>
              <p className="text-sm text-text-muted dark:text-text-muted-dark">
                {t('bulkImport.helper', {
                  defaultValue: 'Supported formats: .xlsx and .csv. Edit the preview inline before starting the import.'
                })}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 rounded-full bg-surface dark:bg-surface-dark border border-border dark:border-border-dark text-text-primary dark:text-text-primary-dark hover:bg-card dark:hover:bg-card-dark"
              aria-label={t('common.actions.close', { defaultValue: 'Close' })}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-6 py-4 border-b border-border dark:border-border-dark flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-button bg-action dark:bg-action-dark text-white font-bold cursor-pointer hover:bg-action-hover dark:hover:bg-action-hover-dark">
                {isParsing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                <span>{t('bulkImport.actions.selectFile', { defaultValue: 'Select file' })}</span>
                <input
                  type="file"
                  accept=".xlsx,.csv"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </label>
              <button
                onClick={handleDownloadTemplate}
                type="button"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-button bg-surface dark:bg-surface-dark border border-border dark:border-border-dark text-text-primary dark:text-text-primary-dark font-bold hover:bg-card dark:hover:bg-card-dark"
              >
                <Download className="w-4 h-4" />
                <span>{t('bulkImport.actions.downloadTemplate', { defaultValue: 'Download template' })}</span>
              </button>
              {fileName && (
                <div className="px-3 py-2 rounded-button bg-surface dark:bg-surface-dark border border-border dark:border-border-dark text-sm text-text-primary dark:text-text-primary-dark">
                  {fileName}
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <div className="px-3 py-2 rounded-button bg-surface dark:bg-surface-dark border border-border dark:border-border-dark text-text-primary dark:text-text-primary-dark">
                {t('bulkImport.summary.rows', { defaultValue: 'Rows: {{count}}', count: validatedRows.length })}
              </div>
              <div className={`px-3 py-2 rounded-button border ${totalErrorCount > 0 ? 'bg-error-bg dark:bg-error-bg-dark border-error-border dark:border-error-border-dark text-error dark:text-error-dark' : 'bg-surface dark:bg-surface-dark border-border dark:border-border-dark text-text-primary dark:text-text-primary-dark'}`}>
                {t('bulkImport.summary.errors', { defaultValue: 'Errors: {{count}}', count: totalErrorCount })}
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-auto">
            {validatedRows.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center p-10 text-center">
                <div className="w-16 h-16 rounded-full bg-surface dark:bg-surface-dark border border-border dark:border-border-dark flex items-center justify-center mb-4">
                  <FileSpreadsheet className="w-7 h-7 text-text-muted dark:text-text-muted-dark" />
                </div>
                <h3 className="text-lg font-semibold text-text-primary dark:text-text-primary-dark mb-2">
                  {t('bulkImport.empty.title', { defaultValue: 'No file loaded yet' })}
                </h3>
                <p className="text-sm text-text-muted dark:text-text-muted-dark max-w-xl">
                  {t('bulkImport.empty.description', {
                    defaultValue: 'Upload a spreadsheet to preview every row, validate the imported values, and correct data inline before writing users and cards into the database.'
                  })}
                </p>
              </div>
            ) : (
              <div className="min-w-max">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-card dark:bg-card-dark border-b border-border dark:border-border-dark z-10">
                    <tr>
                      <th className="px-3 py-3 text-left text-text-primary dark:text-text-primary-dark font-semibold">{t('bulkImport.table.row', { defaultValue: 'Row' })}</th>
                      {IMPORT_FIELDS.map((field) => (
                        <th key={field.key} className="px-3 py-3 text-left text-text-primary dark:text-text-primary-dark font-semibold min-w-[180px]">
                          {t(field.labelKey, { defaultValue: field.defaultLabel })}
                        </th>
                      ))}
                      <th className="px-3 py-3 text-left text-text-primary dark:text-text-primary-dark font-semibold min-w-[220px]">
                        {t('bulkImport.table.validation', { defaultValue: 'Validation' })}
                      </th>
                      <th className="px-3 py-3 text-left text-text-primary dark:text-text-primary-dark font-semibold">
                        {t('common.fields.actions', { defaultValue: 'Actions' })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {validatedRows.map((row) => (
                      <tr key={row.id} className="border-b border-border dark:border-border-dark align-top">
                        <td className="px-3 py-3 text-text-muted dark:text-text-muted-dark font-mono">{row.sourceRow}</td>
                        {IMPORT_FIELDS.map((field) => {
                          const hasError = Boolean(row.errors[field.key]);
                          const commonClasses = `w-full px-3 py-2 rounded-input border bg-input-bg dark:bg-input-bg-dark text-sm focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark ${
                            hasError
                              ? 'border-error-border dark:border-error-border-dark text-error dark:text-error-dark'
                              : 'border-border dark:border-border-dark text-text-primary dark:text-text-primary-dark'
                          }`;

                          if (field.key === 'bio') {
                            return (
                              <td key={field.key} className="px-3 py-3">
                                <textarea
                                  rows={3}
                                  value={row[field.key]}
                                  onChange={(e) => updateCell(row.id, field.key, e.target.value)}
                                  className={commonClasses}
                                />
                                {hasError && <div className="mt-1 text-xs text-error dark:text-error-dark">{row.errors[field.key]}</div>}
                              </td>
                            );
                          }

                          if (field.key === 'showRoute') {
                            return (
                              <td key={field.key} className="px-3 py-3">
                                <select
                                  value={row[field.key]}
                                  onChange={(e) => updateCell(row.id, field.key, e.target.value)}
                                  className={commonClasses}
                                >
                                  <option value="">{t('bulkImport.boolean.default', { defaultValue: 'Default: on' })}</option>
                                  <option value="true">{t('common.states.enabled', { defaultValue: 'Enabled' })}</option>
                                  <option value="false">{t('common.states.disabled', { defaultValue: 'Disabled' })}</option>
                                </select>
                                {hasError && <div className="mt-1 text-xs text-error dark:text-error-dark">{row.errors[field.key]}</div>}
                              </td>
                            );
                          }

                          if (field.key === 'role') {
                            return (
                              <td key={field.key} className="px-3 py-3">
                                <select
                                  value={row[field.key] || 'member'}
                                  onChange={(e) => updateCell(row.id, field.key, e.target.value)}
                                  className={commonClasses}
                                >
                                  <option value="member">{t('common.roles.member', { defaultValue: 'Member' })}</option>
                                  <option value="department_lead">{t('common.roles.departmentLead', { defaultValue: 'Department Lead' })}</option>
                                  <option value="admin">{t('common.roles.admin', { defaultValue: 'Admin' })}</option>
                                  <option value="owner">{t('common.roles.owner', { defaultValue: 'Owner' })}</option>
                                </select>
                                {hasError && <div className="mt-1 text-xs text-error dark:text-error-dark">{row.errors[field.key]}</div>}
                              </td>
                            );
                          }

                          return (
                            <td key={field.key} className="px-3 py-3">
                              <input
                                type={URL_FIELDS.has(field.key) ? 'url' : field.key === 'email' ? 'email' : 'text'}
                                value={row[field.key]}
                                onChange={(e) => updateCell(row.id, field.key, e.target.value)}
                                className={commonClasses}
                              />
                              {hasError && <div className="mt-1 text-xs text-error dark:text-error-dark">{row.errors[field.key]}</div>}
                            </td>
                          );
                        })}
                        <td className="px-3 py-3">
                          {row.isValid ? (
                            <div className="inline-flex items-center gap-2 px-3 py-2 rounded-button bg-surface dark:bg-surface-dark border border-border dark:border-border-dark text-text-primary dark:text-text-primary-dark">
                              <CheckCircle2 className="w-4 h-4" />
                              <span>{t('bulkImport.validation.rowValid', { defaultValue: 'Ready to import' })}</span>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {row.errorMessages.map((message, index) => (
                                <div key={`${row.id}-error-${index}`} className="flex items-start gap-2 text-error dark:text-error-dark text-xs">
                                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                                  <span>{message}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <button
                            onClick={() => removeRow(row.id)}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-button bg-surface dark:bg-surface-dark border border-border dark:border-border-dark text-text-primary dark:text-text-primary-dark hover:bg-card dark:hover:bg-card-dark"
                          >
                            <Trash2 className="w-4 h-4" />
                            <span>{t('bulkImport.actions.removeRow', { defaultValue: 'Remove row' })}</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {serverResult.errors.length > 0 && (
            <div className="px-6 py-4 border-t border-border dark:border-border-dark bg-surface dark:bg-surface-dark max-h-44 overflow-auto">
              <div className="text-sm font-semibold text-text-primary dark:text-text-primary-dark mb-2">
                {t('bulkImport.result.title', { defaultValue: 'Import result' })}
              </div>
              <div className="space-y-2">
                {serverResult.errors.map((error, index) => (
                  <div key={`bulk-import-result-${index}`} className="text-xs text-error dark:text-error-dark">
                    {t('bulkImport.result.rowError', {
                      defaultValue: 'Row {{row}} ({{email}}): {{message}}',
                      row: error.row || '?',
                      email: error.email || t('common.fields.unknown', { defaultValue: 'Unknown' }),
                      message: Array.isArray(error.errors) ? error.errors.join(', ') : (error.error || '')
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="px-6 py-5 border-t border-border dark:border-border-dark flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="text-sm text-text-muted dark:text-text-muted-dark">
              {t('bulkImport.footer.note', {
                defaultValue: 'Imported users receive a generated temporary password. You can change it afterwards in user management.'
              })}
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleClose}
                className="px-4 py-2.5 rounded-button bg-surface dark:bg-surface-dark border border-border dark:border-border-dark text-text-primary dark:text-text-primary-dark font-medium hover:bg-card dark:hover:bg-card-dark"
              >
                {t('common.actions.cancel', { defaultValue: 'Cancel' })}
              </button>
              <button
                onClick={handleImport}
                disabled={isImporting || isParsing || validatedRows.length === 0 || hasErrors}
                className="px-4 py-2.5 rounded-button bg-action dark:bg-action-dark text-white font-bold hover:bg-action-hover dark:hover:bg-action-hover-dark disabled:opacity-50 inline-flex items-center gap-2"
              >
                {isImporting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                <span>
                  {isImporting
                    ? t('bulkImport.actions.importing', { defaultValue: 'Importing...' })
                    : t('bulkImport.actions.import', { defaultValue: 'Import users' })}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
